import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  KeyIcon,
  NoSymbolIcon,
  TrashIcon,
  PlusCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import {
  ConfirmModal,
  FilterToggle,
  PageHeader,
  StatusPill,
} from "../../components/admin/adminPrimitives";

interface Account {
  id: string;
  school_name: string;
  email: string;
  full_name: string | null;
  created_at: string;
}

interface License {
  id: string;
  license_key: string;
  status: string;
  label: string | null;
  purchased_at: string | null;
  trial_ends_at: string | null;
  support_ends_at: string | null;
  created_at: string;
  gift_note: string | null;
}

interface Activation {
  id: string;
  license_id: string;
  machine_fingerprint: string;
  machine_label: string | null;
  activated_at: string;
  deactivated_at: string | null;
}

interface TransferRequest {
  id: string;
  license_id: string;
  status: string;
  reason: string | null;
  created_at: string;
}

interface AllowlistEntry {
  email: string;
  note: string | null;
  created_at: string;
}

interface ActionBanner {
  tone: "success" | "error";
  message: string;
}

type ConfirmAction =
  | { kind: "reset_password" }
  | { kind: "resend_verification" }
  | { kind: "ban" }
  | { kind: "unban" }
  | { kind: "delete" }
  | { kind: "revoke_licence"; licence: License }
  | { kind: "extend_support"; licence: License };

function supportStatusFor(
  license: License,
): "active" | "expiring_soon" | "expired" | "not_applicable" {
  if (!license.support_ends_at || license.status === "trial") {
    return "not_applicable";
  }
  const msLeft = new Date(license.support_ends_at).getTime() - Date.now();
  if (msLeft <= 0) return "expired";
  if (msLeft <= 30 * 86_400_000) return "expiring_soon";
  return "active";
}

function formatDate(s: string | null): string {
  if (!s) return "\u2014";
  return new Date(s).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const [account, setAccount] = useState<Account | null>(null);
  const [licences, setLicences] = useState<License[]>([]);
  const [activations, setActivations] = useState<Activation[]>([]);
  const [transfers, setTransfers] = useState<TransferRequest[]>([]);
  const [allowlist, setAllowlist] = useState<AllowlistEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<ActionBanner | null>(null);
  const [confirmOpen, setConfirmOpen] = useState<ConfirmAction | null>(null);
  const [customerNotice, setCustomerNotice] = useState("");
  useEffect(() => { setCustomerNotice(""); }, [confirmOpen]);
  const [busy, setBusy] = useState(false);
  const [showInactiveLicences, setShowInactiveLicences] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    const [acc, licRows, actRows, trRows, allowRows] = await Promise.all([
      supabase
        .from("accounts")
        .select("id, school_name, email, full_name, created_at")
        .eq("id", id)
        .maybeSingle(),
      supabase
        .from("licenses")
        .select(
          "id, license_key, status, label, purchased_at, trial_ends_at, support_ends_at, created_at, gift_note",
        )
        .eq("account_id", id)
        .order("created_at", { ascending: false }),
      supabase
        .from("activations")
        .select(
          "id, license_id, machine_fingerprint, machine_label, activated_at, deactivated_at",
        )
        .in(
          "license_id",
          // Subquery via a second fetch would be cleaner, but we already
          // fetched licences — filter client-side below.
          [],
        ),
      supabase
        .from("transfer_requests")
        .select("id, license_id, status, reason, created_at")
        .in("license_id", []),
      supabase
        .from("signup_allowlist")
        .select("email, note, created_at")
        .eq("email", "")
        .maybeSingle(),
    ]);

    // If the account load failed we can't do anything useful.
    if (acc.error || !acc.data) {
      setError("Account not found.");
      setLoading(false);
      return;
    }
    setAccount(acc.data as Account);
    setLicences((licRows.data ?? []) as License[]);

    // Second round: now we know the licence IDs, fetch activations and
    // transfer requests for them. Two-step because Supabase's client
    // doesn't expose a subquery builder.
    const licenceIds = (licRows.data ?? []).map((l: Record<string, unknown>) => l.id as string);
    if (licenceIds.length > 0) {
      const [actRows2, trRows2] = await Promise.all([
        supabase
          .from("activations")
          .select(
            "id, license_id, machine_fingerprint, machine_label, activated_at, deactivated_at",
          )
          .in("license_id", licenceIds)
          .order("activated_at", { ascending: false }),
        supabase
          .from("transfer_requests")
          .select("id, license_id, status, reason, created_at")
          .in("license_id", licenceIds)
          .order("created_at", { ascending: false }),
      ]);
      setActivations((actRows2.data ?? []) as Activation[]);
      setTransfers((trRows2.data ?? []) as TransferRequest[]);
    } else {
      setActivations([]);
      setTransfers([]);
    }

    // Real allowlist lookup — we use the account email.
    const allowLookup = await supabase
      .from("signup_allowlist")
      .select("email, note, created_at")
      .eq("email", (acc.data as Account).email.toLowerCase())
      .maybeSingle();
    setAllowlist((allowLookup.data ?? null) as AllowlistEntry | null);

    setLoading(false);
    // Suppress unused warnings for the throwaway first fetch
    void actRows;
    void trRows;
    void allowRows;
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function callAction(body: Record<string, unknown>) {
    setBusy(true);
    setBanner(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "admin-user-action",
        { body },
      );
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      return data;
    } finally {
      setBusy(false);
    }
  }

  async function confirmCurrent() {
    if (!confirmOpen || !account) return;
    if ((confirmOpen.kind === "ban" || confirmOpen.kind === "revoke_licence") && !customerNotice.trim()) {
      setBanner({ tone: "error", message: "Enter a customer-facing reason before confirming." });
      return;
    }
    try {
      switch (confirmOpen.kind) {
        case "reset_password":
          await callAction({
            action: "send_password_reset",
            user_id: account.id,
          });
          setBanner({
            tone: "success",
            message: `Password reset email sent to ${account.email}.`,
          });
          break;

        case "resend_verification":
          await callAction({
            action: "resend_verification",
            user_id: account.id,
          });
          setBanner({
            tone: "success",
            message: `Verification email re-sent to ${account.email}.`,
          });
          break;

        case "ban":
          await callAction({ action: "ban_user", user_id: account.id, customer_notice: customerNotice });
          setBanner({ tone: "success", message: "User banned." });
          break;

        case "unban":
          await callAction({ action: "unban_user", user_id: account.id });
          setBanner({ tone: "success", message: "User unbanned." });
          break;

        case "delete":
          await callAction({
            action: "delete_user",
            user_id: account.id,
            confirm: account.school_name,
          });
          setBanner({
            tone: "success",
            message: "Account deleted. Redirecting to user list…",
          });
          setTimeout(() => {
            window.location.href = "/admin/users";
          }, 1500);
          break;

        case "extend_support":
          await callAction({
            action: "extend_support",
            license_id: confirmOpen.licence.id,
            months: 12,
            confirm: "EXTEND",
          });
          setBanner({
            tone: "success",
            message: `Support on ${confirmOpen.licence.license_key} extended by 12 months.`,
          });
          await load();
          break;

        case "revoke_licence":
          await callAction({
            action: "revoke_licence",
            customer_notice: customerNotice,
            license_id: confirmOpen.licence.id,
            confirm: confirmOpen.licence.license_key,
          });
          setBanner({
            tone: "success",
            message: `Licence ${confirmOpen.licence.license_key} revoked.`,
          });
          await load();
          break;
      }
    } catch (err) {
      setBanner({
        tone: "error",
        message: err instanceof Error ? err.message : "Action failed.",
      });
    } finally {
      setConfirmOpen(null);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest-300 border-t-forest-700" />
      </div>
    );
  }

  if (error || !account) {
    return (
      <div>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1 text-sm font-medium text-forest-600 hover:text-forest-800"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to users
        </Link>
        <p className="mt-6 text-sm text-red-600">{error ?? "Account not found."}</p>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/admin/users"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-forest-600 hover:text-forest-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to users
      </Link>

      <PageHeader
        title={account.school_name || "(unnamed)"}
        description={
          <>
            <span className="text-forest-700">{account.email}</span>
            {account.full_name && (
              <>
                {" \u00b7 "}
                {account.full_name}
              </>
            )}
            {" \u00b7 joined "}
            {formatDate(account.created_at)}
            {allowlist && (
              <>
                {" \u00b7 "}
                <span className="font-medium text-forest-700">
                  on allowlist
                </span>
              </>
            )}
          </>
        }
      />

      {banner && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            banner.tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {banner.message}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <ActionButton
          icon={<EnvelopeIcon className="h-4 w-4" />}
          label="Send password reset"
          onClick={() => setConfirmOpen({ kind: "reset_password" })}
        />
        <ActionButton
          icon={<EnvelopeIcon className="h-4 w-4" />}
          label="Resend verification email"
          onClick={() => setConfirmOpen({ kind: "resend_verification" })}
        />
        <ActionButton
          icon={<NoSymbolIcon className="h-4 w-4" />}
          label="Ban user"
          tone="gold"
          onClick={() => setConfirmOpen({ kind: "ban" })}
        />
        <ActionButton
          icon={<ArrowPathIcon className="h-4 w-4" />}
          label="Unban user"
          onClick={() => setConfirmOpen({ kind: "unban" })}
        />
        <ActionButton
          icon={<TrashIcon className="h-4 w-4" />}
          label="Delete account"
          tone="red"
          onClick={() => setConfirmOpen({ kind: "delete" })}
        />
      </div>

      {/* Licences */}
      <section className="mt-10">
        {(() => {
          const inactiveCount = licences.filter(
            (l) => l.status === "revoked" || l.status === "expired",
          ).length;
          return (
            <div className="flex items-end justify-between gap-3">
              <h2 className="font-display text-lg font-semibold text-forest-900">
                Licences
              </h2>
              {inactiveCount > 0 && (
                <FilterToggle
                  label={`Show revoked & expired (${inactiveCount})`}
                  checked={showInactiveLicences}
                  onChange={setShowInactiveLicences}
                />
              )}
            </div>
          );
        })()}
        <div className="mt-4 space-y-4">
          {(() => {
            const visibleLicences = showInactiveLicences
              ? licences
              : licences.filter(
                  (l) => l.status !== "revoked" && l.status !== "expired",
                );
            if (licences.length === 0) {
              return (
                <div className="paper-card rounded-xl border border-cream-300/60 bg-white px-6 py-10 text-center text-sm text-forest-500">
                  No licences for this account yet.
                </div>
              );
            }
            if (visibleLicences.length === 0) {
              return (
                <div className="paper-card rounded-xl border border-cream-300/60 bg-white px-6 py-10 text-center text-sm text-forest-500">
                  No active licences. Toggle above to show revoked and expired licences.
                </div>
              );
            }
            return visibleLicences.map((licence) => {
              const support = supportStatusFor(licence);
              return (
                <div
                  key={licence.id}
                  className="paper-card rounded-xl border border-cream-300/60 bg-white p-5 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <KeyIcon className="h-4 w-4 text-forest-500" />
                        <code className="rounded bg-cream-100 px-2 py-0.5 font-mono text-xs text-forest-900">
                          {licence.license_key}
                        </code>
                        <StatusPill
                          tone={
                            licence.status === "active"
                              ? "forest"
                              : licence.status === "trial"
                                ? "gold"
                                : licence.status === "expired"
                                  ? "bark"
                                  : "red"
                          }
                        >
                          {licence.status}
                        </StatusPill>
                        {support !== "not_applicable" && (
                          <StatusPill
                            tone={
                              support === "active"
                                ? "forest"
                                : support === "expiring_soon"
                                  ? "gold"
                                  : "red"
                            }
                          >
                            support:{" "}
                            {support === "active"
                              ? `until ${formatDate(licence.support_ends_at)}`
                              : support === "expiring_soon"
                                ? `ends ${formatDate(licence.support_ends_at)}`
                                : `ended ${formatDate(licence.support_ends_at)}`}
                          </StatusPill>
                        )}
                      </div>
                      {licence.label && (
                        <p className="mt-2 text-xs text-forest-500">
                          Note: {licence.label}
                        </p>
                      )}
                      {licence.gift_note && (
                        <p className="mt-2 text-xs text-forest-500">
                          Gifted: {licence.gift_note}
                        </p>
                      )}
                      <p className="mt-2 text-xs text-forest-500">
                        Created {formatDate(licence.created_at)}
                        {licence.purchased_at &&
                          ` \u00b7 purchased ${formatDate(licence.purchased_at)}`}
                        {licence.trial_ends_at &&
                          ` \u00b7 trial ends ${formatDate(licence.trial_ends_at)}`}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() =>
                          setConfirmOpen({
                            kind: "extend_support",
                            licence,
                          })
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-forest-200 px-3 py-1.5 text-xs font-semibold text-forest-700 hover:bg-forest-50"
                      >
                        <PlusCircleIcon className="h-3.5 w-3.5" />
                        +12 months support
                      </button>
                      {licence.status !== "revoked" && (
                        <button
                          onClick={() =>
                            setConfirmOpen({
                              kind: "revoke_licence",
                              licence,
                            })
                          }
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                        >
                          <NoSymbolIcon className="h-3.5 w-3.5" />
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </section>

      {/* Activations */}
      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold text-forest-900">
          Activations
        </h2>
        <div className="paper-card mt-4 rounded-xl border border-cream-300/60 bg-white">
          {activations.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-forest-500">
              No activations.
            </div>
          ) : (
            <ul className="divide-y divide-cream-200">
              {activations.map((a) => (
                <li key={a.id} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-forest-900">
                        {a.machine_label ?? "(unnamed machine)"}
                      </p>
                      <p className="mt-0.5 truncate font-mono text-[11px] text-forest-500">
                        {a.machine_fingerprint.slice(0, 16)}
                        &hellip;
                      </p>
                    </div>
                    <div className="text-right text-xs text-forest-500">
                      <div>
                        {a.deactivated_at ? (
                          <StatusPill tone="bark">deactivated</StatusPill>
                        ) : (
                          <StatusPill tone="forest">active</StatusPill>
                        )}
                      </div>
                      <div className="mt-1">
                        since {formatDate(a.activated_at)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Transfer requests */}
      {transfers.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-lg font-semibold text-forest-900">
            Transfer requests
          </h2>
          <div className="paper-card mt-4 rounded-xl border border-cream-300/60 bg-white">
            <ul className="divide-y divide-cream-200">
              {transfers.map((t) => (
                <li key={t.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm text-forest-900">
                      {t.reason ?? "(no reason given)"}
                    </p>
                    <p className="mt-0.5 text-xs text-forest-500">
                      {formatDate(t.created_at)}
                    </p>
                  </div>
                  <StatusPill
                    tone={
                      t.status === "pending"
                        ? "gold"
                        : t.status === "approved"
                          ? "forest"
                          : "bark"
                    }
                  >
                    {t.status}
                  </StatusPill>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <ConfirmModal
        open={confirmOpen !== null}
        title={confirmOpen ? modalTitle(confirmOpen) : ""}
        description={confirmOpen ? <>
          {modalDescription(confirmOpen, account)}
          {(confirmOpen.kind === "ban" || confirmOpen.kind === "revoke_licence") && <label className="mt-4 block">
            Reason to include in the customer email
            <textarea value={customerNotice} onChange={(e) => setCustomerNotice(e.target.value)} maxLength={1000} required rows={3} className="mt-2 w-full rounded-lg border border-forest-200 p-3" />
            <span className="mt-1 block text-xs">An email with this explanation will be queued when you confirm.</span>
          </label>}
          {confirmOpen.kind === "unban" && <p className="mt-3">The customer will be emailed to confirm their account access is restored.</p>}
        </> : null}
        confirmLabel={confirmOpen ? modalConfirmLabel(confirmOpen) : "Confirm"}
        confirmTone={
          confirmOpen &&
          (confirmOpen.kind === "delete" ||
            confirmOpen.kind === "revoke_licence" ||
            confirmOpen.kind === "ban")
            ? "red"
            : "forest"
        }
        requireTypedConfirm={
          confirmOpen?.kind === "delete"
            ? account.school_name
            : confirmOpen?.kind === "revoke_licence"
              ? confirmOpen.licence.license_key
              : confirmOpen?.kind === "extend_support"
                ? "EXTEND"
                : undefined
        }
        onConfirm={confirmCurrent}
        onCancel={() => setConfirmOpen(null)}
        busy={busy}
      />
    </div>
  );
}

function modalTitle(a: ConfirmAction): string {
  switch (a.kind) {
    case "reset_password":
      return "Send password reset?";
    case "resend_verification":
      return "Resend verification email?";
    case "ban":
      return "Ban this user?";
    case "unban":
      return "Unban this user?";
    case "delete":
      return "Delete this account?";
    case "extend_support":
      return "Extend support by 12 months?";
    case "revoke_licence":
      return "Revoke this licence?";
  }
}

function modalDescription(a: ConfirmAction, account: Account): React.ReactNode {
  switch (a.kind) {
    case "reset_password":
      return (
        <>
          Supabase will email a password reset link to{" "}
          <span className="font-semibold">{account.email}</span>.
        </>
      );
    case "resend_verification":
      return (
        <>
          A fresh verification email will go to{" "}
          <span className="font-semibold">{account.email}</span>.
        </>
      );
    case "ban":
      return (
        <>
          The user will be unable to sign in until explicitly unbanned. Their
          licence keys keep working on installed desktops.
        </>
      );
    case "unban":
      return (
        <>
          The user can sign in again immediately.
        </>
      );
    case "delete":
      return (
        <>
          This permanently removes the account and everything keyed to it
          (licences, activations, transfer history). The change cannot be
          undone.
        </>
      );
    case "extend_support":
      return (
        <>
          Moves <code className="font-mono">{a.licence.license_key}</code> forward
          by 12 months of updates and support. If support is currently active,
          the extension compounds from the existing end date; if expired, it
          starts from today.
        </>
      );
    case "revoke_licence":
      return (
        <>
          The licence becomes inactive and the customer's desktop builds will
          report <code>status: revoked</code> on the next check. Use this for
          chargebacks or terms-of-service violations only.
        </>
      );
  }
}

function modalConfirmLabel(a: ConfirmAction): string {
  switch (a.kind) {
    case "reset_password":
      return "Send reset email";
    case "resend_verification":
      return "Resend verification";
    case "ban":
      return "Ban user";
    case "unban":
      return "Unban user";
    case "delete":
      return "Delete account";
    case "extend_support":
      return "Extend support";
    case "revoke_licence":
      return "Revoke licence";
  }
}

function ActionButton({
  icon,
  label,
  onClick,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "gold" | "red";
}) {
  const toneClasses =
    tone === "red"
      ? "border-red-200 text-red-700 hover:bg-red-50"
      : tone === "gold"
        ? "border-gold-300 text-bark-800 hover:bg-gold-50"
        : "border-forest-200 text-forest-700 hover:bg-forest-50";
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 rounded-lg border bg-white px-4 py-3 text-sm font-semibold shadow-sm transition-colors ${toneClasses}`}
    >
      {icon}
      {label}
    </button>
  );
}
